/**
 * Contact card - Figma `6440:36154`.
 *
 * A title and a line of explanation over a wide, low card: a portrait, the person's name
 * and role, and the direct ways to reach them - LinkedIn, a telephone number, an address.
 *
 * It exists for the landing pages, which are offers sent to one named client. The whole
 * point of the block is that the client can see who owns it, so the links are direct
 * rather than a form.
 *
 * **`shared.person` gained `phone` and `email` for this.** The management grid had only
 * `linkedinUrl`; a second person component would have been two places to add a field.
 * Every row is drawn only when it has been filled in, so a person with no LinkedIn simply
 * has two links.
 *
 * **Repeatable where the design draws one.** A second person stacks as a second card,
 * which is the shape the file already gives us for free.
 *
 * The card's radius is 28px against the file's 24. The radius scale has no 24 - the design
 * reached for a *spacing* token there - so this takes the nearest one that exists rather
 * than inventing a value. Listed in docs/design-tokens.md with the file's other
 * contradictions.
 */
import { SectionShell } from '../primitives';
import type { ContactCardSection, Person } from '../../types/sections';
import { Icon } from '../../components/ui/Icon';
import { Image } from '../../components/ui/Image';
import type { IconName } from '../../components/ui/Icon';
import { offsiteHref } from '../../lib/links';

const ROW = 'inline-flex items-center gap-2.5 px-4 py-2 text-body-md text-brand-500 underline '
  + 'decoration-from-font hover:text-brand-600';

/** The three ways to reach somebody, in the order the design lists them. */
function linksOf(person: Person, linkedinLabel: string) {
  const links: { key: string; icon: IconName; href: string; label: string; size: number }[] = [];

  if (person.linkedinUrl) {
    links.push({ key: 'in', icon: 'linkedin', href: offsiteHref(person.linkedinUrl), label: linkedinLabel, size: 24 });
  }
  if (person.phone) {
    // The label keeps the editor's spacing because that is how a number is read; the
    // `tel:` address cannot have them.
    links.push({ key: 'tel', icon: 'phone', href: `tel:${person.phone.replace(/[^+\d]/g, '')}`, label: person.phone, size: 24 });
  }
  if (person.email) {
    links.push({ key: 'mail', icon: 'mail', href: `mailto:${person.email}`, label: person.email, size: 20 });
  }
  return links;
}

export default function ContactCard({ data }: { data: ContactCardSection }) {
  const people = data.people ?? [];

  return (
    <SectionShell settings={data.settings} name="contact-card">
      {data.heading || data.lead ? (
        <div className="flex max-w-[880px] flex-col gap-5">
          {data.heading ? <h2 className="text-h3 text-ink">{data.heading}</h2> : null}
          {data.lead ? <p className="text-body-md text-ink-muted">{data.lead}</p> : null}
        </div>
      ) : null}

      {people.map((person, i) => {
        const links = linksOf(person, 'LinkedIn');

        return (
          <article
            key={i}
            className="flex flex-wrap items-center gap-6 rounded-xxxl border border-grey-200 bg-white p-3 lg:gap-12"
          >
            <div className="flex items-center gap-10">
              {person.photo ? (
                <Image
                  media={person.photo}
                  alt={person.photo.alternativeText ?? person.name}
                  sizes="116px"
                  className="h-[84px] w-[116px] shrink-0 rounded-m object-cover"
                />
              ) : null}

              <div className="flex flex-col gap-4">
                <p className="text-h5 text-brand-900">{person.name}</p>
                {person.role ? <p className="text-body text-ink-muted">{person.role}</p> : null}
              </div>
            </div>

            {links.length ? (
              <ul className="flex list-none flex-wrap items-center p-0">
                {links.map((link) => (
                  <li key={link.key}>
                    <a
                      href={link.href}
                      {...(link.key === 'in' ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      className={ROW}
                    >
                      <Icon name={link.icon} size={link.size} />
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            ) : null}
          </article>
        );
      })}
    </SectionShell>
  );
}
